import { useState, useRef, useCallback, useEffect } from 'react'
import { useCustomerConfig } from '@store'
import { autocomplete, type AutocompleteResult } from '@services/connectorService'

// =============================================================================
// Types
// =============================================================================

export interface UseConnectorAutocompleteOptions {
  /** The trigger type (e.g., 'consumer-name') */
  triggerType: string
  /** Minimum characters before triggering (overrides config default) */
  minChars?: number
  /** Debounce delay in ms (overrides config default) */
  debounceMs?: number
  /** Callback when a result is selected */
  onSelect?: (result: AutocompleteResult) => void
}

export interface UseConnectorAutocompleteReturn {
  /** Current autocomplete results */
  results: AutocompleteResult[]
  /** Whether a search is in progress */
  loading: boolean
  /** Whether the integration is enabled for this trigger */
  enabled: boolean
  /** Search function - call this on input change */
  search: (query: string) => void
  /** Clear results */
  clear: () => void
  /** Handle result selection */
  handleSelect: (result: AutocompleteResult) => void
}

// Re-export types for convenience
export type { AutocompleteResult }

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook for connector autocomplete functionality.
 *
 * Features:
 * - Debounced search to prevent excessive API calls
 * - Minimum character threshold
 * - Loading state management
 * - Silent failures (returns empty results on error)
 */
export function useConnectorAutocomplete(
  options: UseConnectorAutocompleteOptions
): UseConnectorAutocompleteReturn {
  const { triggerType, minChars, debounceMs, onSelect } = options

  // Get customer config to check if integration is enabled
  const customerConfig = useCustomerConfig()
  const integrationConfig = customerConfig?.connector_triggers
  const triggerConfig = integrationConfig?.autocompleteTriggers?.[triggerType]

  // Determine if enabled
  const enabled = Boolean(integrationConfig?.enabled && triggerConfig?.enabled)

  // Get config values with fallbacks
  const effectiveMinChars = minChars ?? triggerConfig?.minChars ?? 3
  const effectiveDebounceMs = debounceMs ?? triggerConfig?.debounceMs ?? 300

  // State
  const [results, setResults] = useState<AutocompleteResult[]>([])
  const [loading, setLoading] = useState(false)

  // Refs for debouncing and request tracking
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastQueryRef = useRef<string>('')
  const requestIdRef = useRef<number>(0)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  /**
   * Search function with debouncing.
   */
  const search = useCallback(
    (query: string) => {
      // Store the query
      lastQueryRef.current = query

      // Clear previous timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }

      // Clear results if query is too short or not enabled
      if (!enabled || query.length < effectiveMinChars) {
        setResults([])
        setLoading(false)
        return
      }

      // Show loading state
      setLoading(true)

      // Debounce the actual search
      debounceRef.current = setTimeout(async () => {
        // Track this request
        const currentRequestId = ++requestIdRef.current

        // Check if query is still the same
        if (query !== lastQueryRef.current) {
          return
        }

        try {
          const searchResults = await autocomplete(triggerType, query)

          // Only update if this is still the latest request
          if (currentRequestId === requestIdRef.current && query === lastQueryRef.current) {
            setResults(searchResults)
          }
        } catch (error) {
          // Silent failure
          if (currentRequestId === requestIdRef.current) {
            setResults([])
          }
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setLoading(false)
          }
        }
      }, effectiveDebounceMs)
    },
    [enabled, effectiveMinChars, effectiveDebounceMs, triggerType]
  )

  /**
   * Clear results.
   */
  const clear = useCallback(() => {
    setResults([])
    setLoading(false)
    lastQueryRef.current = ''
    requestIdRef.current++

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
  }, [])

  /**
   * Handle result selection.
   */
  const handleSelect = useCallback(
    (result: AutocompleteResult) => {
      clear()
      onSelect?.(result)
    },
    [clear, onSelect]
  )

  return {
    results,
    loading,
    enabled,
    search,
    clear,
    handleSelect,
  }
}

export default useConnectorAutocomplete
